import _ from 'lodash'
import React from 'react'
import classNames from 'classnames'
import { Classes, Icon } from '@blueprintjs/core'
import { IconNames } from '@blueprintjs/icons'
import { Link } from 'react-router-dom'

import { Entity } from 'shared-libs/models/entity'

import apis from 'browser/app/models/apis'
import { Button } from 'browser/components/atomic-elements/atoms/button/button'
import { IBaseProps } from 'browser/components/atomic-elements/atoms/base-props'
import { EntityFooter } from 'browser/components/atomic-elements/atoms/footer/entity-footer'
import { EntityFormBlock } from './entity-form-block'
import { AJVSchemaValidator } from 'shared-libs/components/entity/ajv-validator'
import { creationSourceFromWeb } from 'browser/app/utils/utils'
import { EntityHeader } from './entity-header/entity-header'
import { EntityFormSheetMode } from './entity-form-sheet'
import 'browser/components/atomic-elements/organisms/entity/_entity-form-modal.scss'
import {
  IFormFocusHandle,
  FormFocusHandleContext,
  FormFocusHandleSetter,
} from 'shared-libs/components/ui2/behavior/form-focus-target'
import { FormFocusTargetProvider } from 'browser/components/ui2/behavior/form-focus-target-provider'

interface IEntityFormModalProps extends IBaseProps {
  entity?: Entity
  defaultValue?: object
  entitySchema: object
  onCreate?: (entity: any) => void
  onEdit?: (entity: any) => void
  onSelect?: (entity: any) => void
  uiSchemaPath?: string
  mode: EntityFormSheetMode
  /**
   * Component registry used to render the schema. Defaults to the DW
   * `browser/components` map. Pass `MobileComponents` (from
   * `browser/mobile/components`) when opening the modal from an MW
   * context so form fields render as their MW variants.
   */
  componentsMap?: Record<string, any>
  /** When true, suppresses the "open in new tab" button and applies MW modal styles. */
  isMobileWeb?: boolean
  /**
   * Injected by `<Modal>` at mount — closes the surrounding modal. Not
   * something callers pass explicitly.
   */
  onClose?: () => void
}

interface IEntityFormModalState {
  errors: object
  entity: Entity
  isSaving: boolean
  mode: EntityFormSheetMode
}

/**
 * Modal-shaped counterpart of `EntityFormSheet`. Same header + form body
 * + footer, but presented as a centered dialog (via `<Modal>`) instead
 * of a bottom-anchored slide-up sheet. Used by `FormEntityPreviewCard`
 * click actions and `FormEntitySearchField`'s Create action so the
 * edit / view / create surfaces read as first-class dialogs on both DW
 * and MW.
 *
 * Props mirror `EntityFormSheet` intentionally so call sites can swap
 * the two components without changing their `openOverlay(...)` /
 * `handleCreateEntity` shape.
 */
export class EntityFormModal extends React.Component<
  IEntityFormModalProps,
  IEntityFormModalState
> {
  public static defaultProps: Partial<IEntityFormModalProps> = {
    uiSchemaPath: 'uiSchema.web.entityCreationSection',
    mode: EntityFormSheetMode.Create,
  }

  constructor(props) {
    super(props)
    const { defaultValue, entitySchema, mode } = props
    const entityStore = apis.getStore()
    let entity: Entity
    let modeState = mode
    if (props.entity) {
      entity = props.entity
    } else {
      if (mode !== EntityFormSheetMode.Create) {
        // eslint-disable-next-line no-console
        console.warn(
          `EntityFormModal was rendered with mode '${mode}' but no entity. Defaulting to 'Create' mode.`
        )
      }
      entity = entityStore.createRecord(entitySchema, this.defaultWithCreationProps(defaultValue))
      modeState = EntityFormSheetMode.Create
    }
    this.state = {
      mode: modeState,
      entity,
      errors: {},
      isSaving: false,
    }
  }

  public render() {
    const { uiSchemaPath, componentsMap, className } = this.props
    const { entity, errors } = this.state
    const settings = apis.getSettings()

    return (
      <FormFocusHandleContext.Provider value={this.setFocusHandle}>
        <FormFocusTargetProvider>
          <div className={classNames('c-entityFormModal', className)}>
            {this.renderHeader()}
            <div className='c-entityFormModal-body'>
              <EntityFormBlock
                entity={entity}
                errors={errors}
                onSave={this.handleSave}
                onSelect={this.handleSelect}
                uiSchemaPath={uiSchemaPath}
                settings={settings}
                componentsMap={componentsMap}
              />
            </div>
            {this.renderFooter()}
          </div>
        </FormFocusTargetProvider>
      </FormFocusHandleContext.Provider>
    )
  }

  private focusHandleRef: { current: IFormFocusHandle | null } = { current: null }
  private setFocusHandle: FormFocusHandleSetter = (h) => { this.focusHandleRef.current = h }

  private handleSelect = (entity) => {
    this.props.onSelect?.(entity)
    this.props.onClose?.()
  }

  private runValidations = async (entity: Entity, customValidations: any) => {
    const customValidator = customValidations
      ? await AJVSchemaValidator.compile(customValidations)
      : null

    const errors = await entity.validate(true, customValidator)

    if (_.isEmpty(errors)) return
    this.setState({ errors })
    void this.focusHandleRef.current?.focusFirstError()
    throw new Error('Failed to validate entity before save')
  }

  private defaultWithCreationProps(defaultValue: any) {
    return _.merge({ creationSource: creationSourceFromWeb('Entity Form Modal') }, defaultValue)
  }

  private handleSave = (entity): Promise<any> => {
    const { onCreate, onEdit, uiSchemaPath } = this.props
    const { mode } = this.state

    const validationSchema = entity.resolveSubschemaByPath(uiSchemaPath)?.schema?.validationSchema
    const validationPromise = new Promise((resolve, reject) => {
      if (validationSchema) {
        this.runValidations(entity, validationSchema)
          .then(() => resolve(entity))
          .catch(reject)
      } else {
        resolve(entity)
      }
    })

    return validationPromise.then(() => {
      this.setState({ isSaving: true })
      return entity
        .save()
        .then(() => {
          if (mode === EntityFormSheetMode.Edit) {
            onEdit?.(entity)
          } else if (mode === EntityFormSheetMode.Create) {
            onCreate?.(entity)
          }
          this.props.onClose?.()
        })
        .catch((response) => {
          const errors = _.get(response, 'errors')
          if (_.isNil(errors)) return
          this.setState({ errors, isSaving: false })
          void this.focusHandleRef.current?.focusFirstError()
        })
    })
  }

  private handleCancel = (entity) => {
    entity?.rollback()
    this.props.onClose?.()
  }

  private renderHeader() {
    const { isMobileWeb } = this.props
    const { entity, mode } = this.state

    let title = `Viewing ${entity?.displayName ?? ''}`.trim()
    if (mode === EntityFormSheetMode.Edit) {
      title = `Editing ${entity?.displayName ?? ''}`.trim()
    } else if (mode === EntityFormSheetMode.Create) {
      title = `Creating ${entity?.entityType ?? ''}`.trim()
    }

    // Create mode has no persisted entity to link to — suppress the
    // "open in new tab" affordance so users don't tap into a broken URL.
    // Edit / View both operate on a saved entity, so the link is safe.
    // Also hide on MW which has no second-tab affordance.
    const showNewTab =
      !isMobileWeb &&
      mode !== EntityFormSheetMode.Create &&
      !_.isNil(entity?.uniqueId)

    return (
      <EntityHeader
        showOpenInNewTabButton={false}
        entity={entity}
        className='c-entityFormModal-header'
      >
        {/* Deliberately NOT wearing `c-cardHeader-item` here — that class
            forces `flex-direction: column`, which stacks the title above
            the close button. The custom `-headerContent` alone gives us
            row layout without contention. */}
        <div className='c-entityFormModal-headerContent'>
          <div className='c-entityFormModal-headerText'>
            <h3 className='c-cardHeader-title'>{title}</h3>
            {showNewTab && (
              <Link
                className={classNames(Classes.BUTTON, Classes.MINIMAL, 'c-entityFormModal-newTabButton')}
                to={`/entity/${entity.uniqueId}`}
                target='_blank'
                rel='noopener noreferrer'
                aria-label='Open in new tab'
              >
                <Icon icon={IconNames.SHARE} iconSize={14} />
              </Link>
            )}
          </div>
          <Button
            className={classNames(Classes.MINIMAL, 'c-entityFormModal-closeButton')}
            onClick={this.handleCancel.bind(this, entity)}
            aria-label='Close'
          >
            <Icon icon={IconNames.CROSS} />
          </Button>
        </div>
      </EntityHeader>
    )
  }

  private renderFooter() {
    const { entity, errors, isSaving, mode } = this.state
    const onConfirm = () => this.handleSave(entity)
    const onCancel = () => this.handleCancel(entity)
    return (
      <EntityFooter
        entity={entity}
        errors={errors}
        isPrimaryButtonLoading={isSaving}
        isVisible={true}
        onCancelButtonClick={onCancel}
        onPrimaryButtonClick={mode !== EntityFormSheetMode.View ? onConfirm : undefined}
        cancelButtonText={mode === EntityFormSheetMode.View ? 'Close' : 'Cancel'}
      />
    )
  }
}
